import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { DesktopPreview } from "@v2/components/DesktopPreview";
import { MobilePreview } from "@v2/components/MobilePreview";
import PageRenderer from "@v2/features/page-renderer/components/PageRenderer";
import { useViewportStore } from "@v2/features/page-renderer/store/viewportStore";
import { CANVAS_SIZES } from "@v2/utils/canvas-size";
import useMeasure from "react-use-measure";

type DevicePreviewProps = React.PropsWithChildren<{
  pageRoute: string;
  className?: string;
  targetViewport?: "mobile" | "desktop";
  isScrollable?: boolean;
  hideScrollbars?: boolean;
  isInteractive?: boolean;
  // Note (Ezra, Dec 3 2025): URL to display in the desktop browser mockup's URL bar.
  // Typically the published page URL or shop domain as fallback.
  url?: string;
  mobileClassName?: string;
  desktopClassName?: string;
  maxMobileWidth?: number;
  rendererContainerRef?: (node: HTMLDivElement | null) => void;
  cropToFit?: boolean;
}>;

export const DevicePreview: React.FC<DevicePreviewProps> = ({
  pageRoute,
  className,
  targetViewport,
  isScrollable = false,
  hideScrollbars,
  isInteractive = false,
  url,
  mobileClassName,
  desktopClassName,
  children,
  maxMobileWidth,
  rendererContainerRef,
  cropToFit = false,
}) => {
  const { currentViewport } = useViewportStore();
  const viewport = targetViewport ?? currentViewport;
  const [containerRef, containerBounds] = useMeasure({ offsetSize: true });

  const desktopFrameScale =
    containerBounds.width > 0
      ? containerBounds.width / CANVAS_SIZES.desktop.defaultFrameWidth
      : 1;

  return (
    <div
      ref={containerRef}
      className="h-full w-full"
      style={{
        maxWidth:
          viewport === "mobile" && maxMobileWidth
            ? `${maxMobileWidth}px`
            : undefined,
      }}
    >
      {viewport === "mobile" ? (
        <MobilePreview className={mobileClassName} cropToFit={cropToFit}>
          {children ?? (
            <PageRenderer
              overrideStyleLibrary={null}
              viewingVersionId={null}
              pageRoute={pageRoute}
              className={className}
              isScrollable={isScrollable}
              hideScrollbars={hideScrollbars}
              isInteractive={isInteractive}
              containerRef={rendererContainerRef}
            />
          )}
        </MobilePreview>
      ) : (
        <DesktopPreview
          url={url}
          variant="dark"
          className={twMerge(
            "h-full w-full max-w-full max-h-full overflow-hidden",
            desktopClassName,
          )}
          frameScale={desktopFrameScale}
        >
          {children ?? (
            <PageRenderer
              overrideStyleLibrary={null}
              viewingVersionId={null}
              pageRoute={pageRoute}
              className={className}
              targetViewport="desktop"
              isScrollable={isScrollable}
              hideScrollbars={hideScrollbars}
              isInteractive={isInteractive}
              containerRef={rendererContainerRef}
            />
          )}
        </DesktopPreview>
      )}
    </div>
  );
};
